import { apolloClient } from './graphql_client';
import { gql } from '@apollo/client';
import { markSessionActivity } from './session_activity';
import { getRememberMePreference } from './remember_me';
import { getAuthUserString, isAuthPersisted, setAuthUserString } from './auth_storage';

const STORAGE_KEY_USERNAME = 'wp_relogin_username';
const STORAGE_KEY_PASSWORD = 'wp_relogin_password';
const STORAGE_KEY_REFRESH = 'wp_refresh_token';

let isRefreshingAuth = false;
let refreshWaiters: Array<(ok: boolean) => void> = [];

const notifyWaiters = (ok: boolean) => {
  const waiters = refreshWaiters;
  refreshWaiters = [];
  waiters.forEach((resolve) => resolve(ok));
};

export const storeCredentialsForRelogin = (username: string, password: string): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY_USERNAME, username);
    sessionStorage.setItem(STORAGE_KEY_PASSWORD, password);
  } catch (error) {
    console.warn('Failed to store credentials for relogin:', error);
  }
};

export const storeRefreshToken = (refreshToken: string | null | undefined): void => {
  const persist = isAuthPersisted() || getRememberMePreference();
  try {
    if (refreshToken && persist) {
      localStorage.setItem(STORAGE_KEY_REFRESH, refreshToken);
      sessionStorage.removeItem(STORAGE_KEY_REFRESH);
    } else if (refreshToken) {
      sessionStorage.setItem(STORAGE_KEY_REFRESH, refreshToken);
      localStorage.removeItem(STORAGE_KEY_REFRESH);
    } else {
      localStorage.removeItem(STORAGE_KEY_REFRESH);
      sessionStorage.removeItem(STORAGE_KEY_REFRESH);
    }
  } catch (error) {
    // Ignore storage errors
  }
};

export const clearStoredCredentials = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY_USERNAME);
    sessionStorage.removeItem(STORAGE_KEY_PASSWORD);
    sessionStorage.removeItem(STORAGE_KEY_REFRESH);
    localStorage.removeItem(STORAGE_KEY_REFRESH);
  } catch (error) {
    console.warn('Failed to clear stored credentials:', error);
  }
};

export const hasStoredCredentials = (): boolean => {
  return !!(
    sessionStorage.getItem(STORAGE_KEY_USERNAME) &&
    sessionStorage.getItem(STORAGE_KEY_PASSWORD)
  );
};

export const getStoredRefreshToken = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY_REFRESH) || sessionStorage.getItem(STORAGE_KEY_REFRESH);
  } catch {
    return null;
  }
};

/** Decode JWT exp without verifying signature (client-side expiry check only). */
export const isAccessTokenExpired = (token?: string | null, skewMs = 30_000): boolean => {
  if (!token) return true;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return true;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload?.exp) return false;
    return Date.now() >= payload.exp * 1000 - skewMs;
  } catch {
    return false;
  }
};

export const getAccessTokenFromStorage = (): string | null => {
  try {
    const raw = getAuthUserString();
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.accessToken || null;
  } catch {
    return null;
  }
};

const REFRESH_MUTATION = gql`
  mutation RefreshJwtAuthToken($jwtRefreshToken: String!) {
    refreshJwtAuthToken(input: { jwtRefreshToken: $jwtRefreshToken }) {
      authToken
    }
  }
`;

const tryRefreshWithToken = async (): Promise<boolean> => {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return false;

  const { data } = await apolloClient.mutate<{
    refreshJwtAuthToken?: { authToken?: string };
  }>({
    mutation: REFRESH_MUTATION,
    variables: { jwtRefreshToken: refreshToken },
    errorPolicy: 'none',
  });

  const newToken = data?.refreshJwtAuthToken?.authToken;
  if (!newToken) return false;

  const raw = getAuthUserString();
  if (!raw) return false;
  const authUser = JSON.parse(raw);
  authUser.accessToken = newToken;
  setAuthUserString(JSON.stringify(authUser), isAuthPersisted() || getRememberMePreference());
  markSessionActivity();
  return true;
};

/**
 * Attempt silent auth recovery: refresh JWT first, then password re-login.
 * Concurrent callers wait for the same in-progress attempt.
 */
export const attemptSilentRelogin = async (): Promise<boolean> => {
  if (isRefreshingAuth) {
    return new Promise((resolve) => {
      refreshWaiters.push(resolve);
    });
  }

  const hasRefresh = !!getStoredRefreshToken();
  const hasPassword = hasStoredCredentials();
  if (!hasRefresh && !hasPassword) {
    return false;
  }

  isRefreshingAuth = true;

  try {
    // Prefer refresh token (no password re-auth)
    if (hasRefresh) {
      try {
        const refreshed = await tryRefreshWithToken();
        if (refreshed) {
          isRefreshingAuth = false;
          notifyWaiters(true);
          return true;
        }
      } catch (error) {
        console.warn('JWT refresh failed, trying password re-login...', error);
        localStorage.removeItem(STORAGE_KEY_REFRESH);
      }
    }

    const storedUsername = sessionStorage.getItem(STORAGE_KEY_USERNAME);
    const storedPassword = sessionStorage.getItem(STORAGE_KEY_PASSWORD);
    if (!storedUsername || !storedPassword) {
      isRefreshingAuth = false;
      notifyWaiters(false);
      return false;
    }

    // Password re-login may require email OTP — do not auto-complete that silently.
    const { loginWithWordPress } = await import('./wordpress_auth');
    const result = await loginWithWordPress(storedUsername, storedPassword);
    if (result.status !== 'authenticated') {
      isRefreshingAuth = false;
      notifyWaiters(false);
      return false;
    }

    isRefreshingAuth = false;
    notifyWaiters(true);
    return true;
  } catch (error) {
    console.error('Silent re-login failed:', error);
    clearStoredCredentials();
    isRefreshingAuth = false;
    notifyWaiters(false);
    return false;
  }
};
